import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  FormGroup,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

const PERMISSIONS = [
  { key: 'all', label: 'Full Admin (all areas)', description: 'Full access to everything' },
  { key: 'members', label: 'Members' },
  { key: 'finance', label: 'Finance' },
  { key: 'hr', label: 'HR (Elections, Events, Staff, etc.)' },
  { key: 'assets', label: 'Assets' },
  // { key: 'branches', label: 'Branches' },
  { key: 'education', label: 'Education' },
  { key: 'communication', label: 'Communication' },
  { key: 'reports', label: 'Reports' },
  { key: 'staffs', label: 'Staff Management', description: 'Create and manage other executives' },
  { key: 'audit', label: 'Audit Log', description: 'View admin activity log' },
];

const STAFF_TYPE = { executive: 'executive', newStaff: 'newStaff' };

export default function StaffForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [staffType, setStaffType] = useState(STAFF_TYPE.newStaff);
  const [executivesList, setExecutivesList] = useState([]);
  const [executivesLoading, setExecutivesLoading] = useState(false);
  const [formData, setFormData] = useState({
    executive_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    password_confirmation: '',
    country: '',
    region: '',
    city: '',
    residential_address: '',
    permissions: [],
  });

  useEffect(() => {
    if (id) {
      fetchStaff();
    }
  }, [id]);

  useEffect(() => {
    if (!id && staffType === STAFF_TYPE.executive) {
      fetchExecutives();
    }
  }, [id, staffType]);

  const fetchExecutives = async () => {
    try {
      setExecutivesLoading(true);
      const res = await api.get('/hr/executives');
      const grouped = res.data?.data || [];
      const flat = grouped.flatMap((g) =>
        (g.executives || []).map((e) => ({
          id: e.id,
          first_name: e.first_name,
          last_name: e.last_name,
          email: e.email,
          position_name: g.position?.name || '',
        }))
      );
      setExecutivesList(flat);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load executives');
    } finally {
      setExecutivesLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/staff/${id}`);
      const staff = response.data.data;
      setFormData({
        first_name: staff.first_name || '',
        last_name: staff.last_name || '',
        email: staff.email || '',
        phone: staff.phone || '',
        password: '',
        password_confirmation: '',
        country: staff.country || '',
        region: staff.region || '',
        city: staff.city || '',
        residential_address: staff.residential_address || '',
        permissions: staff.permissions || [],
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleStaffTypeChange = (_, value) => {
    if (value == null) return;
    setStaffType(value);
    if (value === STAFF_TYPE.newStaff) {
      setFormData((prev) => ({
        ...prev,
        executive_id: '',
        first_name: '',
        last_name: '',
        email: '',
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        executive_id: '',
        first_name: '',
        last_name: '',
        email: '',
      }));
    }
  };

  const handleExecutiveSelect = (executiveId) => {
    const exec = executivesList.find((e) => String(e.id) === String(executiveId));
    if (!exec) return;
    setFormData((prev) => ({
      ...prev,
      executive_id: executiveId,
      first_name: exec.first_name,
      last_name: exec.last_name,
      email: exec.email,
    }));
  };

  const handlePermissionChange = (permKey) => {
    setFormData({
      ...formData,
      permissions: formData.permissions.includes(permKey)
        ? formData.permissions.filter((p) => p !== permKey)
        : [...formData.permissions, permKey],
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!id && formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      return;
    }

    if (!id && !formData.password) {
      setError('Password is required for new staff');
      return;
    }

    if (!id && staffType === STAFF_TYPE.executive && !formData.executive_id) {
      setError('Please select an executive');
      return;
    }

    setLoading(true);

    try {
      const data = { ...formData };
      if (id && !data.password) {
        delete data.password;
        delete data.password_confirmation;
      }
      if (!id && staffType === STAFF_TYPE.executive) {
        data.executive_id = formData.executive_id;
      } else if (!id) {
        delete data.executive_id;
      }

      if (id) {
        await api.put(`/staff/${id}`, data);
      } else {
        await api.post('/staff', data);
      }
      const hadPermissions = Array.isArray(data.permissions) && data.permissions.length > 0;
      navigate('/dashboard/staff', {
        state: hadPermissions
          ? {
              message: 'Staff updated successfully. If you changed permissions, the executive may need to refresh the page or log out and log back in to access the new areas.',
            }
          : undefined,
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save staff');
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>{id ? 'Edit Staff' : 'New Staff'}</Box>
          </>
        }
        title={id ? 'Edit Staff' : 'New Staff'}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/staff')}
            variant="outlined"
            sx={{ borderColor: tokens.border, color: tokens.ink }}
          >
            Staff
          </Button>
        }
      />

      <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
          {!id && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Add as Executive or New Staff
              </Typography>
              <ToggleButtonGroup
                value={staffType}
                exclusive
                onChange={handleStaffTypeChange}
                aria-label="staff type"
                size="small"
              >
                <ToggleButton value={STAFF_TYPE.executive} aria-label="executive">
                  <BadgeIcon sx={{ mr: 0.5 }} /> Executive
                </ToggleButton>
                <ToggleButton value={STAFF_TYPE.newStaff} aria-label="new staff">
                  <PersonIcon sx={{ mr: 0.5 }} /> New Staff
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          )}

          {!id && staffType === STAFF_TYPE.executive && (
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Select Executive</InputLabel>
                <Select
                  value={formData.executive_id || ''}
                  label="Select Executive"
                  onChange={(e) => handleExecutiveSelect(e.target.value)}
                  disabled={executivesLoading}
                >
                  {executivesList.map((exec) => (
                    <MenuItem key={exec.id} value={exec.id}>
                      {exec.first_name} {exec.last_name}
                      {exec.position_name ? ` — ${exec.position_name}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Select the executive to create a staff account for. Name and email will be filled from their profile.
              </Typography>
            </Grid>
          )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                disabled={!id && staffType === STAFF_TYPE.executive && !!formData.executive_id}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                disabled={!id && staffType === STAFF_TYPE.executive && !!formData.executive_id}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={!id && staffType === STAFF_TYPE.executive && !!formData.executive_id}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
            </Grid>
            {!id && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!id}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    name="password_confirmation"
                    type="password"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    required={!id}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country"
                name="country"
                value={formData.country}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Region"
                name="region"
                value={formData.region}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Residential Address"
                name="residential_address"
                value={formData.residential_address}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Permissions ({staffType === STAFF_TYPE.executive ? 'Executive' : 'Staff'} can only access areas you assign; revoke by unchecking)
              </Typography>
              <FormGroup>
                <Grid container spacing={1}>
                  {PERMISSIONS.map((p) => (
                    <Grid item xs={12} sm={6} md={4} key={p.key}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.permissions.includes(p.key)}
                            onChange={() => handlePermissionChange(p.key)}
                          />
                        }
                        label={p.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !formData.first_name || !formData.last_name || !formData.email}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard/staff')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
